/**
 * Minimal typed i18n runtime (T9 scaffold) — one catalog, one pattern.
 *
 * Deliberately NOT a library: a Record<string,string> per locale and a typed
 * t() cover everything this app does today, with zero bundle cost. When a
 * second locale genuinely ships, add `de.ts` etc. beside en.ts and register
 * it in CATALOGS — nothing else changes.
 *
 * Resolution order (per QA T9): explicit user preference (User.locale,
 * persisted server-side via PUT /users/profile) → browser Accept-Language
 * (navigator.languages) → 'en'. NEVER country/IP — geography is not language.
 */
import { en } from './en';

export type MessageKey = keyof typeof en;

const CATALOGS: Record<string, Record<MessageKey, string>> = { en };

let activeLocale = 'en';

export function resolveLocale(userPreference?: string | null): string {
  const candidates: Array<string | undefined | null> = [
    userPreference,
    ...(typeof navigator !== 'undefined' ? Array.from(navigator.languages ?? []) : []),
  ];
  for (const candidate of candidates) {
    const base = candidate?.toLowerCase().split('-')[0];
    if (base && CATALOGS[base]) return base;
  }
  return 'en';
}

/** Set the active locale from the signed-in user's stored preference. */
export function setLocale(userPreference?: string | null): void {
  activeLocale = resolveLocale(userPreference);
}

/** Translate a catalog key, with {var} interpolation. */
export function t(key: MessageKey, vars?: Record<string, string | number>): string {
  const template = CATALOGS[activeLocale]?.[key] ?? en[key];
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match
  );
}
